import { Controller } from "@hotwired/stimulus"
import { showConfirmModal } from "../lib/confirm_modal.mjs"
import { showToast } from "../lib/toasts.mjs"

export default class extends Controller {
  static targets = ["email", "feedback", "copyButton"]

  connect() {
    this.edited = false
    this.selection = [...this.element.querySelectorAll('input[name="providers[]"]:checked')].map(el => el.value)
  }

  markEdited() { this.edited = true }

  async confirmRegeneration(event) {
    if (this.regenerating) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    if (!this.edited || this.replacementApproved) {
      this.replacementApproved = false
      return
    }
    event.preventDefault()
    event.stopImmediatePropagation()
    if (this.confirming) return
    this.confirming = true
    const form = event.target
    const accepted = await showConfirmModal({
      title: "Replace edited email?", message: "Changing notable providers replaces your edited email with a new preview.",
      confirmLabel: "Replace email", allowSkip: false
    })
    this.confirming = false
    if (!this.element.isConnected) return
    if (accepted) {
      this.replacementApproved = true
      form.requestSubmit()
    } else {
      this.restoreSelection(form)
    }
  }

  restoreSelection(form) {
    form.querySelectorAll('input[name="providers[]"]').forEach(input => { input.checked = this.selection.includes(input.value) })
    const picker = form.querySelector('[data-controller="multi-select"]')
    this.application.getControllerForElementAndIdentifier(picker, "multi-select")?.update()
  }

  startRegeneration(event) {
    this.regenerating = true
    event.target.inert = true
    this.emailTarget.setAttribute("contenteditable", "false")
    this.emailTarget.setAttribute("aria-busy", "true")
    this.copyButtonTarget.disabled = true
    this.feedbackTarget.textContent = "Updating preview…"
  }

  preserveDraftOnError(event) {
    // Turbo renders HTTP errors too. Stop the response before it can replace
    // the frame; submit-end will unlock the existing draft and show feedback.
    if (!event.detail.fetchResponse.succeeded) event.preventDefault()
  }

  finishRegeneration(event) {
    // Success replaces this controller with a fresh frame. On failure keep the
    // edited draft and restore the selection that actually produced it.
    if (event.detail.success || !this.element.isConnected) return
    this.regenerating = false
    event.target.inert = false
    this.emailTarget.setAttribute("contenteditable", "true")
    this.emailTarget.removeAttribute("aria-busy")
    this.copyButtonTarget.disabled = false
    this.restoreSelection(event.target)
    this.feedback("Could not update the preview. Your email edits were kept. Please try again.", "error")
  }

  paste(event) {
    event.preventDefault()
    const template = document.createElement("template")
    const html = event.clipboardData.getData("text/html")
    if (html) template.innerHTML = html
    else event.clipboardData.getData("text/plain").split(/\r?\n/).forEach((line, index) => {
      if (index) template.content.append(document.createElement("br"))
      template.content.append(document.createTextNode(line))
    })
    const clean = document.createElement("template")
    clean.innerHTML = this.emailHtml(template)
    const selection = window.getSelection()
    if (!selection?.rangeCount) return
    const range = selection.getRangeAt(0)
    if (!this.emailTarget.contains(range.commonAncestorContainer)) return
    // The wrapper carries export typography, but would split an inline paste
    // into a block inside the user's sentence. Insert only its safe children.
    const fragment = document.createDocumentFragment()
    fragment.append(...clean.content.firstElementChild.childNodes)
    const inserted = fragment.lastChild
    if (!inserted) return
    range.deleteContents()
    range.insertNode(fragment)
    range.setStartAfter(inserted)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
    this.markEdited()
  }

  emailHtml(source = this.emailTarget) {
    // Export a standalone fragment: app classes/editor chrome must never reach the email client.
    // Parse in an inert document. Never adopt unsanitized nodes into the live
    // document: even a detached image can load and invoke its error handler.
    const template = document.createElement("template")
    template.innerHTML = source.innerHTML
    const wrapper = template.content
    const allowed = new Set(["DIV", "P", "H1", "H2", "H3", "H4", "STRONG", "B", "EM", "I", "U", "S", "SPAN", "BR", "HR", "UL", "OL", "LI", "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TH", "TD", "A"])
    wrapper.querySelectorAll("script,style,link,meta,iframe,object,embed,img,svg,form,input,button").forEach(el => el.remove())
    const font = "font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#34303b;"
    const styles = {
      DIV: font,
      P: `${font}line-height:150%;margin:0 0 12px;`,
      H1: `${font}font-size:22px;font-weight:bold;line-height:130%;margin:0 0 16px;`,
      H2: `${font}font-size:20px;font-weight:bold;line-height:130%;margin:0 0 16px;`,
      H3: `${font}font-size:16px;font-weight:bold;line-height:140%;margin:24px 0 10px;`,
      H4: `${font}font-weight:bold;line-height:150%;margin:18px 0 8px;`,
      TABLE: "border-collapse:collapse;width:100%;margin:12px 0;mso-table-lspace:0pt;mso-table-rspace:0pt;",
      TH: `${font}font-size:13px;font-weight:bold;line-height:150%;border:1px solid #d7dbe4;padding:8px 10px;text-align:left;background-color:#f0f3f7;`,
      TD: `${font}font-size:13px;line-height:150%;border:1px solid #d7dbe4;padding:8px 10px;text-align:left;`,
      UL: `${font}list-style-type:disc;margin:0 0 12px;padding-left:24px;`,
      OL: `${font}list-style-type:decimal;margin:0 0 12px;padding-left:24px;`,
      LI: `${font}line-height:150%;margin:0 0 6px;`,
      A: "color:#4c56e0;text-decoration:underline;"
    }
    Array.from(wrapper.querySelectorAll("*")).forEach(el => {
      if (!allowed.has(el.tagName)) {
        el.replaceWith(...el.childNodes)
        return
      }
      const href = el.tagName === "A" ? el.getAttribute("href") : null
      const spans = ["colspan", "rowspan"].map(key => [key, el.getAttribute(key)])
      Array.from(el.attributes).forEach(attr => el.removeAttribute(attr.name))
      if (href && /^(https?:|mailto:)/i.test(href)) el.setAttribute("href", href)
      if (styles[el.tagName]) el.setAttribute("style", styles[el.tagName])
      if (["TH", "TD"].includes(el.tagName)) {
        el.setAttribute("align", "left")
        el.setAttribute("valign", "top")
        spans.forEach(([key, value]) => { if (/^[1-9]\d*$/.test(value || "")) el.setAttribute(key, value) })
        if (el.tagName === "TH") el.setAttribute("bgcolor", "#f0f3f7")
      }
      if (el.tagName === "TABLE") {
        el.setAttribute("width", "100%")
        el.setAttribute("cellpadding", "0")
        el.setAttribute("cellspacing", "0")
        el.setAttribute("border", "0")
      }
    })
    const exported = document.createElement("div")
    exported.setAttribute("style", `${font}max-width:680px;`)
    exported.append(wrapper)
    return exported.outerHTML
  }

  emailText() {
    const wrapper = document.createElement("div")
    wrapper.innerHTML = this.emailHtml()
    const text = (node, depth = 0) => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.replace(/\s+/g, " ")
      if (node.nodeType !== Node.ELEMENT_NODE) return ""
      if (node.tagName === "BR") return "\n"
      if (node.tagName === "TABLE") {
        return Array.from(node.rows).map(row => Array.from(row.cells).map(cell => cell.textContent.trim()).join("\t")).join("\n") + "\n\n"
      }
      if (["UL", "OL"].includes(node.tagName)) {
        return Array.from(node.children).map(child => text(child, depth)).join("") + "\n"
      }
      if (node.tagName === "LI") {
        const children = Array.from(node.childNodes)
        const isList = child => ["UL", "OL"].includes(child.tagName)
        const label = children.filter(child => !isList(child)).map(child => text(child, depth)).join("").trim()
        const nested = children.filter(isList).map(child => text(child, depth + 1)).join("").trimEnd()
        return `${"  ".repeat(depth)}• ${label}\n${nested ? nested + "\n" : ""}`
      }
      const contents = Array.from(node.childNodes).map(child => text(child, depth)).join("")
      return /^(DIV|P|H[1-4])$/.test(node.tagName) ? `${contents.trim()}\n\n` : contents
    }
    return text(wrapper).replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
  }

  feedback(message, tone) {
    this.feedbackTarget.textContent = message
    showToast(message, tone ? { tone } : {})
  }

  async copyEmail() {
    if (this.regenerating) return
    const plain = this.emailText()
    try {
      await navigator.clipboard.write([new ClipboardItem({
        "text/html": new Blob([this.emailHtml()], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" })
      })])
      this.feedback("Email body copied with formatting.")
    } catch {
      try {
        await navigator.clipboard.writeText(plain)
        this.feedback("Email body copied as plain text.")
      } catch {
        this.feedback("Clipboard access is unavailable. Select and copy the email text.", "error")
      }
    }
  }
}
